import { useEffect, useMemo, useState, useRef } from "react";
import api, { checkFileSize } from "@/lib/api";
import { UploadLimitNote } from "@/components/UploadLimitNote";
import { toast } from "sonner";
import { Plus, Trash2, Upload, Edit2, Star, Heart, Circle, Square, Diamond, Triangle, Hexagon, GripVertical, ChevronDown, ChevronRight, Eye, EyeOff, QrCode, Download, X } from "lucide-react";
import RichText from "@/components/RichText";
import { useAuth } from "@/lib/auth";
import { ReadonlyArchives } from "@/components/ReadonlyArchives";
import { getActiveToken } from "@/lib/sessionToken";
import { ProductStatus } from "@/components/ProductStatus";
import { DefaultPictograms } from "@/components/DefaultPictograms";
import { PictogramCategoryDropdown } from "@/components/PictogramCategoryDropdown";
import { PictogramImage } from "@/components/PictogramImage";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { pictogramFileError } from "@/lib/pictogramUpload";
import "./pictogram-manager.css";

const TABS = ["brands", "categories", "characteristics", "pictograms"];
const TAB_LABEL = { brands: "Brand", categories: "Categorie", characteristics: "Caratteristiche", pictograms: "Pittogrammi" };
const RATING_ICONS = {
  star: Star, heart: Heart,
  circle: Circle, square: Square, diamond: Diamond, triangle: Triangle, hexagon: Hexagon,
};
const RATING_ICON_OPTIONS = [
  { key: "star", label: "Stella", Icon: Star },
  { key: "heart", label: "Cuore", Icon: Heart },
  { key: "circle", label: "Pallino", Icon: Circle },
  { key: "square", label: "Quadrato", Icon: Square },
  { key: "diamond", label: "Rombo", Icon: Diamond },
  { key: "triangle", label: "Triangolo", Icon: Triangle },
  { key: "hexagon", label: "Esagono", Icon: Hexagon },
];

export default function Archives() {
  const [tab, setTab] = useState("brands");
  const { canWrite } = useAuth();
  if (!canWrite) return <ReadonlyArchives />;
  return (
    <div className="workflow-ux p-4 pt-16 md:p-8 lg:p-12" data-testid="archives-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Configurazione</div>
        <h1 className="font-serif text-3xl md:text-4xl">Archivi</h1>
      </div>
      <div className="flex border-b border-[#E5E5E0] mb-8 overflow-x-auto whitespace-nowrap">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
                   className={`tab-btn ${tab === t ? "active" : ""}`}
                   data-testid={`archive-tab-${t}`}>
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>
      {tab === "brands" && <BrandsManager />}
      {tab === "categories" && <CategoriesManager />}
      {tab === "characteristics" && <CharacteristicsManager />}
      {tab === "pictograms" && <PictogramsManager />}
    </div>
  );
}

function BrandsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [qrModal, setQrModal] = useState(null); // brand object
  const [delBrand, setDelBrand] = useState(null); // { brand, count }
  const [delBusy, setDelBusy] = useState(false);
  const [maxBrands, setMaxBrands] = useState(0);
  const load = () => api.get("/brands").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/me/usage").then(r => setMaxBrands(r.data.max_brands || 0)).catch(()=>{});
  }, []);
  const empty = { name: "", description: "", website: "", company_name: "", address: "",
    vat_number: "", email: "", phone: "",
    instagram_url: "", facebook_url: "", linkedin_url: "", x_url: "",
    primary_color_hex: "#1A1A1A",
    secondary_color_hex: "#F5F2EC", rating_icon: "star",
    logo_file_id: null, is_active: true };
  const save = async () => {
    if (!editing.name) return toast.error("Nome richiesto");
    let saved;
    if (editing.id) saved = (await api.put(`/brands/${editing.id}`, editing)).data;
    else saved = (await api.post("/brands", editing)).data;
    toast.success("Brand salvato");
    setEditing(null); load();
    return saved;
  };
  const askDel = async (b) => {
    let count = 0;
    try { const r = await api.get(`/brands/${b.id}/usage`); count = r.data.product_count || 0; } catch { /* keep 0 */ }
    setDelBrand({ brand: b, count });
  };
  const confirmDel = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/brands/${delBrand.brand.id}`);
      toast.success("Brand eliminato");
      setDelBrand(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setDelBusy(false); }
  };
  // New raster uploads are normalized to WebP; PDFs remain documents.
  const uploadLogoForBrand = async (file, brandId) => {
    if (!file) return null;
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post(`/brands/${brandId}/logo`, fd);
    return r.data.file_id;
  };
  // Inline upload from the form: if brand exists -> upload; if new -> save first, then upload.
  const handleFormLogoUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!checkFileSize(f)) { e.target.value = ""; return; }
    // Validate type
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"];
    if (!allowed.includes(f.type)) {
      toast.error("Formato non supportato. Usa PNG, JPG, WEBP o PDF.");
      return;
    }
    setUploadingLogo(true);
    try {
      let brandId = editing.id;
      if (!brandId) {
        // Save the brand first so we can attach a logo
        if (!editing.name?.trim()) {
          toast.error("Inserisci almeno il nome prima di caricare il logo");
          setUploadingLogo(false);
          return;
        }
        const created = (await api.post("/brands", editing)).data;
        brandId = created.id;
        setEditing({ ...editing, id: brandId });
      }
      const fileId = await uploadLogoForBrand(f, brandId);
      setEditing(prev => ({ ...prev, id: brandId, logo_file_id: fileId }));
      toast.success("Logo caricato");
      load();
    } catch (err) {
      toast.error("Errore caricamento logo");
    } finally {
      setUploadingLogo(false);
      e.target.value = "";
    }
  };
  // Quick upload from the table row (when a brand has no logo yet, or to replace it)
  const uploadLogoFromTable = async (e, brandId) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!checkFileSize(f)) { e.target.value = ""; return; }
    await uploadLogoForBrand(f, brandId);
    toast.success("Logo aggiornato");
    load();
    e.target.value = "";
  };

  const atBrandLimit = maxBrands > 0 && items.length >= maxBrands;
  const handleNewBrand = () => {
    if (atBrandLimit) {
      toast.error(`Hai raggiunto il limite di ${maxBrands} brand previsto dal tuo abbonamento. Contatta l'amministratore per aumentarlo.`);
      return;
    }
    setEditing({...empty});
  };

  return (
    <div className="luxury-card p-6" data-testid="brands-manager">
      <div className="flex flex-wrap justify-between items-start gap-3 mb-5">
        <div>
          <h3 className="font-serif text-2xl">Brand</h3>
          <div className="text-xs mt-1" data-testid="brand-limit-info">
            {maxBrands > 0 ? (
              <span className={atBrandLimit ? "text-[#B24040] font-medium" : "text-[#707070]"}>
                Brand: {items.length}/{maxBrands} · {Math.max(0, maxBrands - items.length)} rimanenti
              </span>
            ) : (
              <span className="text-[#707070]">Brand: {items.length} · illimitato</span>
            )}
          </div>
        </div>
        <button onClick={handleNewBrand} className={`btn-primary flex items-center ${atBrandLimit ? "opacity-60" : ""}`} data-testid="brand-new-btn">
          <Plus className="w-3 h-3 mr-2" />Nuovo brand
        </button>
      </div>
      {editing && (
        <div className="bg-[#F4F4F0] p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="label-tiny">Logo brand</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 border border-[#E5E5E0] bg-white flex items-center justify-center overflow-hidden rounded-sm">
                {editing.logo_file_id ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${editing.logo_file_id}`}
                       className="max-w-full max-h-full object-contain" alt="Logo" data-testid="brand-logo-preview" />
                ) : (
                  <Upload className="w-6 h-6 text-[#C5B39B]" strokeWidth={1.5}/>
                )}
              </div>
              <div className="flex-1">
                <label className="btn-ghost inline-flex items-center cursor-pointer" data-testid="brand-logo-upload-btn">
                  <Upload className="w-3 h-3 mr-2" strokeWidth={1.5}/>
                  {uploadingLogo ? "Caricamento..." : (editing.logo_file_id ? "Sostituisci logo" : "Carica logo")}
                  <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp,application/pdf"
                         data-testid="brand-logo-input"
                         onChange={handleFormLogoUpload} disabled={uploadingLogo} />
                </label>
                <div className="text-xs text-[#707070] mt-2">PNG, JPG, WEBP o PDF · max consigliato 1MB. Il logo apparirà al centro del QR code.</div>
                <UploadLimitNote />
              </div>
            </div>
          </div>
          <input className="input-luxury" placeholder="Nome *" value={editing.name}
                 data-testid="brand-input-name"
                 onChange={(e)=>setEditing({...editing, name: e.target.value})} />
          <input className="input-luxury" placeholder="Ragione sociale" value={editing.company_name||""}
                 data-testid="brand-input-company"
                 onChange={(e)=>setEditing({...editing, company_name: e.target.value})} />
          <input className="input-luxury" placeholder="P.IVA" value={editing.vat_number||""}
                 data-testid="brand-input-vat"
                 onChange={(e)=>setEditing({...editing, vat_number: e.target.value})} />
          <input className="input-luxury" placeholder="Sito web" value={editing.website||""}
                 data-testid="brand-input-website"
                 onChange={(e)=>setEditing({...editing, website: e.target.value})} />
          <input className="input-luxury" placeholder="Email" value={editing.email||""}
                 data-testid="brand-input-email"
                 onChange={(e)=>setEditing({...editing, email: e.target.value})} />
          <input className="input-luxury" placeholder="Telefono" value={editing.phone||""}
                 data-testid="brand-input-phone"
                 onChange={(e)=>setEditing({...editing, phone: e.target.value})} />
          <input className="input-luxury sm:col-span-2" placeholder="Indirizzo" value={editing.address||""}
                 data-testid="brand-input-address"
                 onChange={(e)=>setEditing({...editing, address: e.target.value})} />
          <div className="sm:col-span-2 -mb-2 mt-2 text-[10px] tracking-luxury text-[#707070] uppercase">Social</div>
          <input className="input-luxury" placeholder="Instagram URL"
                 value={editing.instagram_url||""}
                 data-testid="brand-input-instagram"
                 onChange={(e)=>setEditing({...editing, instagram_url: e.target.value})} />
          <input className="input-luxury" placeholder="Facebook URL"
                 value={editing.facebook_url||""}
                 data-testid="brand-input-facebook"
                 onChange={(e)=>setEditing({...editing, facebook_url: e.target.value})} />
          <input className="input-luxury" placeholder="LinkedIn URL"
                 value={editing.linkedin_url||""}
                 data-testid="brand-input-linkedin"
                 onChange={(e)=>setEditing({...editing, linkedin_url: e.target.value})} />
          <input className="input-luxury" placeholder="X (Twitter) URL"
                 value={editing.x_url||""}
                 data-testid="brand-input-x"
                 onChange={(e)=>setEditing({...editing, x_url: e.target.value})} />
          <textarea className="input-luxury sm:col-span-2" placeholder="Descrizione" rows={3} value={editing.description||""}
                    data-testid="brand-input-description"
                    onChange={(e)=>setEditing({...editing, description: e.target.value})} />
          <div>
            <ColorPickerField
              label="Colore primario"
              value={editing.primary_color_hex}
              onChange={(v)=>setEditing({...editing, primary_color_hex: v})}
              testId="brand-input-color"
              fallback="#1A1A1A"
            />
          </div>
          <div>
            <ColorPickerField
              label="Colore secondario (sfondo scheda)"
              value={editing.secondary_color_hex}
              onChange={(v)=>setEditing({...editing, secondary_color_hex: v})}
              testId="brand-input-secondary-color"
              fallback="#F5F2EC"
            />
          </div>
          <div>
            <label className="label-tiny">Icona rating</label>
            <div className="flex flex-wrap gap-2" data-testid="brand-input-rating-icon">
              {RATING_ICON_OPTIONS.map(opt => {
                const selected = (editing.rating_icon || "star") === opt.key;
                const Ico = opt.Icon;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setEditing({ ...editing, rating_icon: opt.key })}
                    data-testid={`rating-icon-opt-${opt.key}`}
                    title={opt.label}
                    className={`w-10 h-10 flex items-center justify-center border transition ${selected
                      ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#FAF9F6]"
                      : "border-[#E5E5E0] text-[#1A1A1A] hover:border-[#1A1A1A]"}`}>
                    <Ico className="w-4 h-4" strokeWidth={1.6}
                         style={{ color: selected ? undefined : (editing.primary_color_hex || "#1A1A1A") }}/>
                  </button>
                );
              })}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={editing.is_active} data-testid="brand-input-active"
                   onChange={(e)=>setEditing({...editing, is_active: e.target.checked})} />
            Brand attivo
            <ProductStatus tone={editing.is_active ? "success" : "danger"} testId="brand-edit-status">
              {editing.is_active ? "Attivo" : "Disattivo"}
            </ProductStatus>
          </label>
          <div className="sm:col-span-2 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="btn-ghost" data-testid="brand-cancel-btn">Annulla</button>
            <button onClick={save} className="btn-primary" data-testid="brand-save-btn">Salva</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto"><table className="table-luxury" data-testid="brands-table">
        <thead><tr><th>Logo</th><th>Nome</th><th>Colore</th><th>Rating</th><th>Stato</th><th>Catalogo</th><th></th></tr></thead>
        <tbody>
          {items.map(b => {
            const Icon = RATING_ICONS[b.rating_icon || "star"];
            return (
              <tr key={b.id} data-testid={`brand-row-${b.id}`}>
                <td>
                  <label className="cursor-pointer relative inline-block group" title="Carica o sostituisci logo">
                    {b.logo_file_id ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${b.logo_file_id}`}
                           className="w-10 h-10 object-contain border border-[#E5E5E0] rounded-sm bg-white" alt={b.name} />
                    ) : (
                      <div className="w-10 h-10 border border-dashed border-[#C5B39B] rounded-sm flex items-center justify-center bg-white text-[#707070] hover:border-[#1A1A1A] hover:text-[#1A1A1A]">
                        <Upload className="w-4 h-4" strokeWidth={1.5} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center rounded-sm">
                      <Upload className="w-3.5 h-3.5 text-white" strokeWidth={2} />
                    </div>
                    <input type="file" className="hidden"
                           accept="image/png,image/jpeg,image/webp,application/pdf"
                           data-testid={`brand-logo-quick-${b.id}`}
                           onChange={(e)=>uploadLogoFromTable(e, b.id)} />
                  </label>
                </td>
                <td className="font-medium">{b.name}</td>
                <td><span className="inline-block w-6 h-6 rounded-sm border" style={{background: b.primary_color_hex}}/></td>
                <td><Icon className="w-4 h-4 fill-[#1A1A1A]"/></td>
                <td><ProductStatus tone={b.is_active ? "success" : "danger"} testId={`brand-status-${b.id}`}>
                  {b.is_active ? "Attivo" : "Disattivo"}
                </ProductStatus></td>
                <td>
                  {b.public_slug ? (
                    <div className="flex items-center gap-2">
                      <a href={`/b/${b.public_slug}`} target="_blank" rel="noreferrer"
                         className="text-xs tracking-luxury uppercase text-[#1A1A1A] hover:text-[#707070] font-mono normal-case"
                         data-testid={`brand-public-link-${b.id}`}>
                        /b/{b.public_slug}
                      </a>
                      <button onClick={()=>{
                        const url = `${window.location.origin}/b/${b.public_slug}`;
                        navigator.clipboard.writeText(url);
                        toast.success("Link copiato");
                      }}
                              className="text-[10px] tracking-luxury uppercase text-[#707070] hover:text-[#1A1A1A]"
                              data-testid={`brand-copy-link-${b.id}`}>
                        Copia
                      </button>
                      <button onClick={()=>setQrModal(b)}
                              title="QR code catalogo"
                              className="text-[#707070] hover:text-[#1A1A1A] inline-flex items-center gap-1 text-[10px] tracking-luxury uppercase"
                              data-testid={`brand-qr-open-${b.id}`}>
                        <QrCode className="w-3.5 h-3.5" strokeWidth={1.5}/>QR
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-[#A0A0A0]">—</span>
                  )}
                </td>
                <td className="text-right">
                  <button onClick={()=>setEditing(b)} className="text-xs px-2 hover:text-[#1A1A1A]" data-testid={`brand-edit-${b.id}`}><Edit2 className="w-3 h-3 inline"/></button>
                  <button onClick={()=>askDel(b)} className="text-xs px-2 text-[#8C3A3A]" data-testid={`brand-delete-${b.id}`}><Trash2 className="w-3 h-3 inline"/></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {qrModal && (
        <BrandQrModal brand={qrModal} onClose={()=>setQrModal(null)} onRegen={load}/>
      )}
      {delBrand && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="brand-delete-modal">
          <div className="bg-white w-full max-w-md border border-[#E5E5E0] shadow-xl">
            <div className="px-6 py-4 border-b border-[#F0F0EA]">
              <h3 className="font-serif text-lg">Elimina brand</h3>
            </div>
            <div className="p-6 text-sm text-[#1A1A1A] leading-relaxed">
              {delBrand.count > 0 ? (
                <>Il brand <strong>«{delBrand.brand.name}»</strong> è associato a <strong>{delBrand.count}</strong> prodott{delBrand.count === 1 ? "o" : "i"} e non può essere eliminato. Riassegna prima i prodotti a un altro brand.</>
              ) : (
                <>Sei sicuro di voler eliminare il brand <strong>«{delBrand.brand.name}»</strong>? L'operazione non è reversibile.</>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#F0F0EA]">
              <button onClick={()=>setDelBrand(null)} className="btn-ghost text-sm" data-testid="brand-delete-cancel">
                {delBrand.count > 0 ? "Chiudi" : "Annulla"}
              </button>
              {delBrand.count === 0 && (
                <button onClick={confirmDel} disabled={delBusy} className="btn-primary text-sm !bg-[#8C3A3A]" data-testid="brand-delete-confirm">
                  {delBusy ? "Eliminazione..." : "Elimina brand"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BrandQrModal({ brand, onClose, onRegen }) {
  const [b, setB] = useState(brand);
  const [busy, setBusy] = useState(false);
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const publicUrl = `${window.location.origin}/b/${b.public_slug}`;

  const dl = async (fmt) => {
    const token = getActiveToken();
    const res = await fetch(
      `${apiBase}/api/brands/${b.id}/qr/download?format=${fmt}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const blob = await res.blob();
    // Use the filename provided by the backend (Content-Disposition), so all
    // formats share the same friendly name (e.g. "OneLove.svg").
    const cd = res.headers.get("content-disposition") || "";
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const fname = m ? m[1] : `qr-catalog.${fmt}`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fname;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const regen = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/brands/${b.id}/qr/regenerate`);
      setB(prev => ({ ...prev,
        qr_png_file_id: r.data.qr_png_file_id,
        qr_svg_file_id: r.data.qr_svg_file_id,
        qr_pdf_file_id: r.data.qr_pdf_file_id,
      }));
      toast.success("QR rigenerato");
      onRegen && onRegen();
    } catch { toast.error("Errore rigenerazione QR"); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiato");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6"
         onClick={onClose} data-testid="brand-qr-modal">
      <div className="bg-white max-w-md w-full p-8 luxury-card" onClick={e=>e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">QR Catalogo</div>
            <h3 className="font-serif text-2xl">{b.name}</h3>
          </div>
          <button onClick={onClose} className="text-[#707070] hover:text-[#1A1A1A] text-2xl leading-none"
                  data-testid="brand-qr-close">×</button>
        </div>

        <div className="flex flex-col items-center gap-4 mb-6">
          {b.qr_png_file_id ? (
            <img src={`${apiBase}/api/files/${b.qr_png_file_id}`}
                 className="w-56 h-56 border border-[#E5E5E0]"
                 alt="QR catalogo" data-testid="brand-qr-image"/>
          ) : (
            <div className="w-56 h-56 border border-dashed border-[#C5B39B] flex items-center justify-center text-xs text-[#707070]">
              QR non ancora generato
            </div>
          )}
          <code className="text-xs bg-[#F4F4F0] px-3 py-2 rounded-sm break-all text-center"
                data-testid="brand-qr-url">{publicUrl}</code>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <button onClick={()=>dl("png")} className="btn-ghost flex items-center justify-center"
                  data-testid="brand-qr-download-png">
            <Download className="w-3 h-3 mr-2"/>PNG
          </button>
          <button onClick={()=>dl("svg")} className="btn-ghost flex items-center justify-center"
                  data-testid="brand-qr-download-svg">
            <Download className="w-3 h-3 mr-2"/>SVG
          </button>
          <button onClick={()=>dl("pdf")} className="btn-ghost flex items-center justify-center"
                  data-testid="brand-qr-download-pdf">
            <Download className="w-3 h-3 mr-2"/>PDF
          </button>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} className="btn-ghost flex-1" data-testid="brand-qr-copy-url">
            Copia link
          </button>
          <button onClick={regen} disabled={busy}
                  className="btn-ghost flex-1 disabled:opacity-50"
                  data-testid="brand-qr-regen">
            {busy ? "Rigenerazione..." : "Rigenera"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoriesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cosm_cat_collapsed") || "{}"); }
    catch { return {}; }
  });
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);

  const load = () => api.get("/categories").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggleCollapse = (id) => {
    setCollapsed(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem("cosm_cat_collapsed", JSON.stringify(next));
      return next;
    });
  };

  const empty = (parent_id = null) => ({
    name: "", parent_id, order_index: 999, is_active: true,
    description_richtext: "", warnings_richtext: "", usage_richtext: "",
    weight_g: null, length_cm: null, width_cm: null, height_cm: null,
    pictogram_ids: [], characteristic_values: []
  });

  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Nome richiesto");
    const payload = { ...editing };
    ["weight_g", "length_cm", "width_cm", "height_cm"].forEach(k => {
      payload[k] = payload[k] === "" || payload[k] === null ? null : Number(payload[k]);
    });
    payload.order_index = Number(payload.order_index) || 0;
    if (editing.id) await api.put(`/categories/${editing.id}`, payload);
    else await api.post("/categories", payload);
    toast.success("Categoria salvata");
    setEditing(null); load();
  };

  const del = async (id, name) => {
    if (!confirm(`Eliminare "${name}"?`)) return;
    try { await api.delete(`/categories/${id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const toggleActive = async (cat) => {
    try {
      await api.put(`/categories/${cat.id}/active`, { is_active: !cat.is_active });
      load();
    } catch { toast.error("Errore"); }
  };

  const reorder = async (parent_id, orderedIds) => {
    try {
      await api.put("/categories/reorder", { ids: orderedIds, parent_id });
      load();
    } catch { toast.error("Errore riordino"); load(); }
  };

  const tops = useMemo(
    () => items.filter(c => !c.parent_id).sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
    [items]
  );
  const childrenOf = (id) => items.filter(c => c.parent_id === id)
                                    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

  const onDragStart = (id) => (e) => {
    setDragId(id);
    e.stopPropagation();
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };
  const onDragOver = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (overId !== id) setOverId(id);
  };
  const onDrop = (id, parentId) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragId || dragId === id) { setDragId(null); setOverId(null); return; }
    const dragged = items.find(c => c.id === dragId);
    const target = items.find(c => c.id === id);
    if (!dragged || !target || dragged.parent_id !== target.parent_id) {
      toast.error("Si possono riordinare solo elementi dello stesso livello");
      setDragId(null); setOverId(null);
      return;
    }
    const siblings = items.filter(c => c.parent_id === parentId)
                            .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
    const fromIdx = siblings.findIndex(c => c.id === dragId);
    const toIdx = siblings.findIndex(c => c.id === id);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...siblings];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    reorder(parentId, next.map(c => c.id));
    setDragId(null); setOverId(null);
  };
  const onDragEnd = () => { setDragId(null); setOverId(null); };

  return (
    <div className="luxury-card p-6" data-testid="categories-manager">
      <div className="flex flex-wrap justify-between items-start mb-5 gap-4">
        <div>
          <h3 className="font-serif text-2xl">Categorie & Sottocategorie</h3>
          <div className="text-xs text-[#707070] mt-1">
            Trascina la maniglia <GripVertical className="inline w-3 h-3 -mt-0.5" strokeWidth={1.5}/> per riordinare. Click sulla freccia per espandere/comprimere.
          </div>
        </div>
          <div className="flex flex-wrap gap-2 flex-shrink-0">
          <button onClick={() => {
            const allCollapsed = tops.every(t => collapsed[t.id]);
            const next = {};
            tops.forEach(t => { next[t.id] = !allCollapsed; });
            setCollapsed(next);
            localStorage.setItem("cosm_cat_collapsed", JSON.stringify(next));
          }} className="btn-ghost text-xs" data-testid="cat-collapse-all-btn">
            {tops.every(t => collapsed[t.id]) ? "Espandi tutto" : "Comprimi tutto"}
          </button>
          <button onClick={() => setEditing(empty(null))} className="btn-primary flex items-center" data-testid="cat-new-btn">
            <Plus className="w-3 h-3 mr-2" />Nuova categoria
          </button>
        </div>
      </div>

      {editing && (
        <CategoryEditForm editing={editing} setEditing={setEditing} save={save} parents={tops} />
      )}

      <div className="space-y-3">
        {tops.length === 0 && (
          <div className="text-center py-10 text-sm text-[#707070]">
            Nessuna categoria. Crea la prima cliccando &quot;Nuova categoria&quot;.
          </div>
        )}
        {tops.map(top => {
          const subs = childrenOf(top.id);
          const isCollapsed = !!collapsed[top.id];
          return (
            <div key={top.id}
                 draggable
                 onDragStart={onDragStart(top.id)}
                 onDragOver={onDragOver(top.id)}
                 onDrop={onDrop(top.id, null)}
                 onDragEnd={onDragEnd}
                 className={`border bg-white rounded-sm transition ${dragId === top.id ? "opacity-40" : ""} ${overId === top.id && dragId && dragId !== top.id ? "border-[#1A1A1A] border-2" : "border-[#E5E5E0]"}`}
                 data-testid={`cat-card-${top.id}`}>
              <div className="flex items-center px-3 py-3 gap-2">
                <GripVertical className="w-4 h-4 text-[#C5B39B] cursor-move flex-shrink-0" strokeWidth={1.5}/>
                <button onClick={() => toggleCollapse(top.id)} className="p-0.5 hover:bg-[#F4F4F0] rounded flex-shrink-0"
                        type="button" title={isCollapsed ? "Espandi" : "Comprimi"}
                        data-testid={`cat-toggle-${top.id}`}>
                  {isCollapsed ? <ChevronRight className="w-4 h-4" strokeWidth={1.5}/> : <ChevronDown className="w-4 h-4" strokeWidth={1.5}/>}
                </button>
                <div className="flex-1 flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                  <span className={`font-medium tracking-wide truncate ${top.is_active ? "" : "text-[#A09988] line-through"}`}>{top.name}</span>
                  <ProductStatus tone={top.is_active ? "success" : "danger"} testId={`cat-status-${top.id}`}>
                    {top.is_active ? "Attiva" : "Disattiva"}
                  </ProductStatus>
                  <span className="text-xs text-[#A09988] flex-shrink-0 hidden sm:inline" data-testid={`cat-subcount-${top.id}`}>{subs.length} sottocategori{subs.length === 1 ? "a" : "e"}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleActive(top)} title={top.is_active ? "Disattiva" : "Attiva"}
                          className="p-1.5 rounded hover:bg-[#F4F4F0] text-[#707070] hover:text-[#1A1A1A]"
                          data-testid={`cat-active-${top.id}`}>
                    {top.is_active ? <Eye className="w-3.5 h-3.5" strokeWidth={1.5}/> : <EyeOff className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                  </button>
                  <button onClick={() => setEditing({ ...top })} title="Modifica"
                          className="p-1.5 rounded hover:bg-[#F4F4F0] text-[#707070] hover:text-[#1A1A1A]"
                          data-testid={`cat-edit-${top.id}`}>
                    <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                  </button>
                  <button onClick={() => del(top.id, top.name)} title="Elimina"
                          className="p-1.5 rounded hover:bg-[#F4F4F0] text-[#707070] hover:text-[#8C3A3A]"
                          data-testid={`cat-delete-${top.id}`}>
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                  </button>
                </div>
              </div>
              {!isCollapsed && (
                <div className="pl-12 pr-3 pb-3 border-t border-[#F4F4F0]">
                  <div className="divide-y divide-[#F4F4F0]">
                    {subs.length === 0 && (
                      <div className="py-3 text-xs text-[#A09988] italic">Nessuna sottocategoria</div>
                    )}
                    {subs.map(sub => (
                      <div key={sub.id}
                           draggable
                           onDragStart={onDragStart(sub.id)}
                           onDragOver={onDragOver(sub.id)}
                           onDrop={onDrop(sub.id, top.id)}
                           onDragEnd={onDragEnd}
                           className={`flex items-center py-2 gap-2 transition group ${dragId === sub.id ? "opacity-40" : ""} ${overId === sub.id && dragId && dragId !== sub.id ? "bg-[#F4F4F0]" : ""}`}
                           data-testid={`subcat-row-${sub.id}`}>
                        <GripVertical className="w-3.5 h-3.5 text-[#C5B39B] cursor-move flex-shrink-0" strokeWidth={1.5}/>
                        <span className="text-[#A09988] text-xs flex-shrink-0">└</span>
                        <span className={`flex-1 text-sm truncate min-w-0 ${sub.is_active ? "" : "text-[#A09988] line-through"}`}>{sub.name}</span>
                        <ProductStatus tone={sub.is_active ? "success" : "danger"} testId={`subcat-status-${sub.id}`}>
                          {sub.is_active ? "Attiva" : "Disattiva"}
                        </ProductStatus>
                        <div className="flex items-center gap-0.5 opacity-50 group-hover:opacity-100 transition flex-shrink-0">
                          <button onClick={() => toggleActive(sub)} title={sub.is_active ? "Disattiva" : "Attiva"}
                                  className="p-1 rounded hover:bg-white text-[#707070] hover:text-[#1A1A1A]"
                                  data-testid={`subcat-active-${sub.id}`}>
                            {sub.is_active ? <Eye className="w-3 h-3" strokeWidth={1.5}/> : <EyeOff className="w-3 h-3" strokeWidth={1.5}/>}
                          </button>
                          <button onClick={() => setEditing({ ...sub })} title="Modifica"
                                  className="p-1 rounded hover:bg-white text-[#707070] hover:text-[#1A1A1A]"
                                  data-testid={`subcat-edit-${sub.id}`}>
                            <Edit2 className="w-3 h-3" strokeWidth={1.5}/>
                          </button>
                          <button onClick={() => del(sub.id, sub.name)} title="Elimina"
                                  className="p-1 rounded hover:bg-white text-[#707070] hover:text-[#8C3A3A]"
                                  data-testid={`subcat-delete-${sub.id}`}>
                            <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setEditing(empty(top.id))}
                           className="mt-3 text-xs text-[#707070] hover:text-[#1A1A1A] flex items-center"
                           data-testid={`subcat-add-${top.id}`}>
                    <Plus className="w-3 h-3 mr-1" strokeWidth={2}/>Aggiungi sottocategoria
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CategoryEditForm({ editing, setEditing, save, parents }) {
  return (
    <div className="bg-[#F4F4F0] p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-sm border border-[#E5E5E0]"
         data-testid="cat-edit-form">
      <div className="sm:col-span-2 flex items-baseline justify-between">
        <h4 className="font-serif text-lg">
          {editing.id ? "Modifica categoria" : (editing.parent_id ? "Nuova sottocategoria" : "Nuova categoria")}
        </h4>
        {editing.parent_id && (
          <div className="text-xs text-[#707070]">
            Parent: <strong>{parents.find(p => p.id === editing.parent_id)?.name || "—"}</strong>
          </div>
        )}
      </div>
      <input className="input-luxury" placeholder="Nome *" value={editing.name}
             data-testid="cat-input-name" autoFocus
             onChange={(e)=>setEditing({...editing, name: e.target.value})} />
      <select className={`input-luxury ${!editing.parent_id ? "product-ux-select-empty" : ""}`} value={editing.parent_id || ""}
              data-testid="cat-input-parent"
              onChange={(e)=>setEditing({...editing, parent_id: e.target.value || null})}>
        <option value="">— Categoria principale —</option>
        {parents.filter(c => c.id !== editing.id).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <div className="sm:col-span-2">
        <label className="label-tiny">Descrizione (ereditabile)</label>
        <RichText value={editing.description_richtext||""}
                  onChange={(v)=>setEditing({...editing, description_richtext: v})}
                  testId="cat-input-description"
                  placeholder="Descrizione della categoria/sottocategoria..." minHeight={120} />
      </div>
      <div className="sm:col-span-2">
        <label className="label-tiny">Avvertenze (ereditabili)</label>
        <RichText value={editing.warnings_richtext||""}
                  onChange={(v)=>setEditing({...editing, warnings_richtext: v})}
                  placeholder="Avvertenze..." minHeight={100} testId="cat-input-warnings" />
      </div>
      <div className="sm:col-span-2">
        <label className="label-tiny">Modo d&apos;uso (ereditabile)</label>
        <RichText value={editing.usage_richtext||""}
                  onChange={(v)=>setEditing({...editing, usage_richtext: v})}
                  placeholder="Modo d'uso..." minHeight={100} testId="cat-input-usage" />
      </div>
      <label className="flex items-center gap-2 text-sm sm:col-span-2">
        <input type="checkbox" checked={editing.is_active} data-testid="cat-input-active"
               onChange={(e)=>setEditing({...editing, is_active: e.target.checked})} /> Attiva
        <ProductStatus tone={editing.is_active ? "success" : "danger"} testId="cat-edit-status">
          {editing.is_active ? "Attiva" : "Disattiva"}
        </ProductStatus>
      </label>
      <div className="sm:col-span-2 flex justify-end gap-3">
        <button onClick={() => setEditing(null)} className="btn-ghost" data-testid="cat-cancel-btn">Annulla</button>
        <button onClick={save} className="btn-primary" data-testid="cat-save-btn">Salva</button>
      </div>
    </div>
  );
}

function CharacteristicsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/characteristics").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const empty = { name: "", description_internal: "", order_index: 0, is_active: true };
  const save = async () => {
    if (!editing.name) return toast.error("Nome richiesto");
    if (editing.id) await api.put(`/characteristics/${editing.id}`, editing);
    else await api.post("/characteristics", editing);
    setEditing(null); load();
  };
  return (
    <div className="luxury-card p-6" data-testid="char-manager">
      <div className="flex flex-wrap justify-between gap-3 mb-5">
        <h3 className="font-serif text-2xl">Caratteristiche</h3>
        <button onClick={() => setEditing({...empty})} className="btn-primary flex items-center" data-testid="char-new-btn">
          <Plus className="w-3 h-3 mr-2"/>Nuova
        </button>
      </div>
      {editing && (
        <div className="bg-[#F4F4F0] p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input className="input-luxury" placeholder="Nome *" value={editing.name}
                 data-testid="char-input-name"
                 onChange={(e)=>setEditing({...editing, name: e.target.value})} />
          <input className="input-luxury" type="number" placeholder="Ordine" value={editing.order_index}
                 data-testid="char-input-order"
                 onChange={(e)=>setEditing({...editing, order_index: Number(e.target.value)})} />
          <textarea className="input-luxury sm:col-span-2" rows={2} placeholder="Descrizione interna"
                     data-testid="char-input-description"
                     value={editing.description_internal||""}
                     onChange={(e)=>setEditing({...editing, description_internal: e.target.value})} />
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={editing.is_active} data-testid="char-input-active"
                   onChange={(e)=>setEditing({...editing, is_active: e.target.checked})}/>Attiva
            <ProductStatus tone={editing.is_active ? "success" : "danger"} testId="char-edit-status">
              {editing.is_active ? "Attiva" : "Disattiva"}
            </ProductStatus>
          </label>
          <div className="sm:col-span-2 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="btn-ghost" data-testid="char-cancel-btn">Annulla</button>
            <button onClick={save} className="btn-primary" data-testid="char-save-btn">Salva</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto"><table className="table-luxury">
        <thead><tr><th>Nome</th><th>Ordine</th><th>Stato</th><th></th></tr></thead>
        <tbody>
          {items.map(c => (
            <tr key={c.id} data-testid={`char-row-${c.id}`}>
              <td className="font-medium">{c.name}</td>
              <td>{c.order_index}</td>
              <td><ProductStatus tone={c.is_active ? "success" : "danger"} testId={`char-status-${c.id}`}>
                {c.is_active ? "Attiva" : "Disattiva"}
              </ProductStatus></td>
              <td className="text-right">
                <button onClick={()=>setEditing(c)} className="text-xs px-2" data-testid={`char-edit-${c.id}`} title="Modifica"><Edit2 className="w-3 h-3 inline"/></button>
                <button onClick={async()=>{await api.delete(`/characteristics/${c.id}`); load();}} className="text-xs px-2 text-[#8C3A3A]" data-testid={`char-delete-${c.id}`} title="Elimina"><Trash2 className="w-3 h-3 inline"/></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function PictogramsManager() {
  const [items, setItems] = useState([]);
  const [pcats, setPcats] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [creatingPicto, setCreatingPicto] = useState(false);
  const pictoTriggerRef = useRef(null);
  const [pendingIconFile, setPendingIconFile] = useState(null);
  const [pendingIconUrl, setPendingIconUrl] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [delPicto, setDelPicto] = useState(null); // { picto, count }
  const [delCat, setDelCat] = useState(null);      // { id, name }
  const [delBusy, setDelBusy] = useState(false);
  const [savingPicto, setSavingPicto] = useState(false);
  const [savingCat, setSavingCat] = useState(false);
  const [pictoSaveError, setPictoSaveError] = useState("");
  const [categorySaveError, setCategorySaveError] = useState("");
  useEffect(() => {
    if (!pendingIconFile) { setPendingIconUrl(null); return; }
    const url = URL.createObjectURL(pendingIconFile);
    setPendingIconUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingIconFile]);
  const load = () => Promise.all([
    api.get("/pictograms").then(r => setItems(r.data)),
    api.get("/pictogram-categories").then(r => setPcats(r.data)),
  ]).then(() => setLoadError("")).catch(() => setLoadError("Impossibile caricare i pittogrammi."));
  useEffect(() => { load(); }, []);
  const ownItems = items.filter(p => !p.is_default && !p.is_default_derivative);
  const visibleCats = pcats.filter(c => !c.is_default_derivative);
  const displayCategoryId = (p) => pcats.find(c => c.id === p.category_id)?.default_category_id || p.category_id;
  const empty = { name: "", order_index: 0, is_active: true, icon_file_id: null, category_id: null, color_hex: "#1A1A1A" };
  const emptyCat = { name: "", order_index: 0 };
  const restorePictoFocus = () => {
    const target = pictoTriggerRef.current?.isConnected ? pictoTriggerRef.current :
      document.querySelector(`[data-testid="picto-card-${editing?.id}"]`) || document.querySelector('[data-testid="picto-new-btn"]');
    target?.focus();
  };
  const closePictoEditor = () => { setEditing(null); setPendingIconFile(null); setTimeout(restorePictoFocus, 0); };
  const openPictoEditor = (picto, event) => {
    pictoTriggerRef.current = event.currentTarget;
    setCreatingPicto(false); setPictoSaveError(""); setPendingIconFile(null); setEditing({ ...picto });
  };
  const save = async () => {
    if (!editing.name?.trim()) return toast.error("Nome richiesto");
    if (creatingPicto && !editing.category_id) return toast.error("Seleziona una categoria");
    setSavingPicto(true); setPictoSaveError("");
    try {
      let pictoId = editing.id;
      if (pictoId) await api.put(`/pictograms/${pictoId}`, editing);
      else {
        const created = (await api.post("/pictograms", editing)).data;
        pictoId = created.id;
        setEditing(prev => ({ ...prev, id: pictoId }));
        setCreatingPicto(false);
      }
      if (pendingIconFile) await uploadIconForPicto(pendingIconFile, pictoId);
      await load(); closePictoEditor(); toast.success("Pittogramma salvato");
    } catch (err) {
      const message = typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Impossibile salvare il pittogramma. Riprova.";
      setPictoSaveError(message); toast.error(message);
    } finally { setSavingPicto(false); }
  };
  const saveCat = async () => {
    if (!editingCat.name?.trim()) return toast.error("Nome categoria richiesto");
    setSavingCat(true); setCategorySaveError("");
    try {
      if (editingCat.id) await api.put(`/pictogram-categories/${editingCat.id}`, editingCat);
      else await api.post("/pictogram-categories", editingCat);
      setEditingCat(null); await load(); toast.success("Categoria salvata");
    } catch (err) {
      const message = typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Impossibile salvare la categoria. Riprova.";
      setCategorySaveError(message); toast.error(message);
    } finally { setSavingCat(false); }
  };
  const askDeletePicto = async (p) => {
    let count = 0;
    try { const r = await api.get(`/pictograms/${p.id}/usage`); count = r.data.product_count || 0; } catch { /* keep 0 */ }
    setDelPicto({ picto: p, count });
  };
  const confirmDeletePicto = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/pictograms/${delPicto.picto.id}`);
      toast.success("Pittogramma eliminato");
      setDelPicto(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore eliminazione"); }
    finally { setDelBusy(false); }
  };
  const confirmDelCat = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/pictogram-categories/${delCat.id}`);
      toast.success("Categoria eliminata. I pittogrammi sono stati liberati.");
      setDelCat(null); load();
    } catch { toast.error("Errore eliminazione"); }
    finally { setDelBusy(false); }
  };

  // Drag & Drop: move a pictogram to another category via optimistic PUT
  const movePictogramToCategory = async (pictoId, newCategoryId) => {
    const picto = ownItems.find(p => p.id === pictoId);
    if (!picto) return;
    if ((picto.category_id || null) === (newCategoryId || null)) return; // no-op
    // Optimistic update
    setItems(prev => prev.map(p => p.id === pictoId ? { ...p, category_id: newCategoryId } : p));
    try {
      await api.put(`/pictograms/${pictoId}`, {
        name: picto.name,
        order_index: picto.order_index || 0,
        is_active: picto.is_active !== false,
        icon_file_id: picto.icon_file_id || null,
        category_id: newCategoryId,
        color_hex: picto.color_hex || null,
      });
      toast.success("Pittogramma spostato");
    } catch {
      toast.error("Errore spostamento");
      load();
    }
  };

  const onDragStart = (e, pictoId) => {
    setDraggingId(pictoId);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", pictoId); } catch {}
  };
  const onDragEnd = () => { setDraggingId(null); setDragOverKey(null); };
  const onDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverKey !== key) setDragOverKey(key);
  };
  const onDragLeave = (e, key) => {
    // only clear if we truly left this drop zone
    if (dragOverKey === key) setDragOverKey(null);
  };
  const onDrop = (e, newCatId) => {
    e.preventDefault();
    const pid = draggingId || e.dataTransfer.getData("text/plain");
    setDraggingId(null); setDragOverKey(null);
    if (pid) movePictogramToCategory(pid, newCatId);
  };

  // Upload icon for a SAVED pictogram
  const uploadIconForPicto = async (file, pictoId) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post(`/pictograms/${pictoId}/icon`, fd);
    return r.data.file_id;
  };

  // A form selection stays local: neither icon nor category changes until Salva.
  const handleFormIconUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!checkFileSize(f)) { e.target.value = ""; return; }
    const fileError = pictogramFileError(f, true);
    if (fileError) {
      toast.error(fileError);
      e.target.value = "";
      return;
    }
    setPendingIconFile(f);
  };

  // Quick upload from the grid card
  const uploadIconFromCard = async (e, pictoId) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!checkFileSize(f)) { e.target.value = ""; return; }
    const fileError = pictogramFileError(f, true);
    if (fileError) { toast.error(fileError); e.target.value = ""; return; }
    try {
      await uploadIconForPicto(f, pictoId);
      toast.success("Icona aggiornata"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore caricamento icona"); }
    e.target.value = "";
  };

  return (
    <div className="picto-manager p-2 md:p-4" data-testid="picto-manager">
      {loadError && <div role="alert" className="text-sm text-[#B91C1C] mb-6" data-testid="picto-load-error">
        {loadError} <button type="button" onClick={load} className="underline" data-testid="picto-load-retry">Riprova</button>
      </div>}
      <DefaultPictograms items={items} categories={pcats} reload={load} />
      <section data-testid="tenant-pictograms-own">
      <div className="flex flex-wrap justify-between gap-3 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-luxury text-[#707070] mb-2">La tua raccolta</div>
          <h3 className="font-serif text-2xl">Pittogrammi personali</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => { setCategorySaveError(""); setEditingCat({...emptyCat}); }} className="btn-ghost flex items-center" data-testid="pcat-new-btn">
            <Plus className="w-3 h-3 mr-2"/>Nuova categoria
          </button>
          <button onClick={e => { pictoTriggerRef.current = e.currentTarget; setCreatingPicto(true); setPictoSaveError(""); setPendingIconFile(null); setEditing({...empty}); }} className="btn-primary flex items-center" data-testid="picto-new-btn">
            <Plus className="w-3 h-3 mr-2"/>Nuovo pittogramma
          </button>
        </div>
      </div>

      {/* Category editor */}
      {editingCat && (
        <div className="bg-[#F4F4F0] p-5 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="pcat-edit-form">
          {categorySaveError && <p role="alert" className="sm:col-span-3 text-sm text-[#B91C1C]" data-testid="pcat-save-error">{categorySaveError}</p>}
          <input className="input-luxury sm:col-span-2" placeholder="Nome categoria *" value={editingCat.name}
                 data-testid="pcat-input-name"
                 onChange={(e)=>setEditingCat({...editingCat, name: e.target.value})} />
          <input className="input-luxury" type="number" placeholder="Ordine" value={editingCat.order_index || 0}
                 data-testid="pcat-input-order"
                 onChange={(e)=>setEditingCat({...editingCat, order_index: Number(e.target.value)})} />
          <div className="sm:col-span-3 flex justify-end gap-3">
            <button onClick={() => setEditingCat(null)} className="btn-ghost" data-testid="pcat-cancel-btn">Annulla</button>
            <button onClick={saveCat} disabled={savingCat} className="btn-primary" data-testid="pcat-save-btn">{savingCat ? "Salvataggio..." : "Salva categoria"}</button>
          </div>
        </div>
      )}
      {editing && (
        <Dialog open onOpenChange={open => { if (!open && !savingPicto) closePictoEditor(); }}>
        <DialogContent showCloseButton={false} onCloseAutoFocus={e => { e.preventDefault(); restorePictoFocus(); }}
          className="w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-sm bg-white p-5 sm:p-7"
          data-testid={creatingPicto ? "picto-create-modal" : `picto-edit-modal-${editing.id}`}>
          <DialogHeader className="text-left pr-7">
            <DialogTitle className="font-serif text-2xl">{creatingPicto ? "Nuovo pittogramma" : "Modifica pittogramma"}</DialogTitle>
            <DialogDescription className="sr-only">Nome, categoria e opzioni del pittogramma personale.</DialogDescription>
          </DialogHeader>
          <button type="button" onClick={closePictoEditor} disabled={savingPicto} aria-label="Chiudi" title="Chiudi"
            className="absolute right-5 top-5 text-[#525252] hover:text-[#1A1A1A]" data-testid="picto-modal-close">
            <X className="w-5 h-5" />
          </button>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0" data-testid="picto-edit-form">
          {pictoSaveError && <p role="alert" className="sm:col-span-2 text-sm text-[#B91C1C]" data-testid="picto-save-error">{pictoSaveError}</p>}
          <div className="sm:col-span-2">
            <label htmlFor="picto-input-name" className="label-tiny">Nome *</label>
            <input id="picto-input-name" className="input-luxury" placeholder="Nome *" value={editing.name}
              data-testid="picto-input-name" onChange={e => setEditing({...editing, name: e.target.value})} />
          </div>
          <div className="sm:col-span-2">
            <PictogramCategoryDropdown id="picto-input-category" value={displayCategoryId(editing)}
              options={visibleCats.map(c => ({ ...c, label: `${c.is_default ? "Default" : "Personale"} · ${c.name}` }))}
              required={creatingPicto} allowNone={!creatingPicto} testId="picto-input-category"
              onChange={value => setEditing({...editing, category_id: value})} />
          </div>
          {!editing.is_default_derivative && <div className="sm:col-span-2">
            <label className="label-tiny">Icona pittogramma</label>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 border border-[#E5E5E0] bg-white flex items-center justify-center overflow-hidden rounded-sm">
                {pendingIconUrl ? (
                  <img src={pendingIconUrl} className="w-16 h-16 object-contain" alt="Anteprima icona selezionata" data-testid="picto-icon-preview" />
                ) : editing.icon_file_id ? (
                  <PictogramImage pictogram={editing} className="w-16 h-16" alt="Icona"
                       testId="picto-icon-preview" />
                ) : (
                  <Upload className="w-5 h-5 text-[#C5B39B]" strokeWidth={1.5}/>
                )}
              </div>
              <div className="flex-1">
                <label className="btn-ghost inline-flex items-center cursor-pointer" data-testid="picto-icon-upload-btn">
                  <Upload className="w-3 h-3 mr-2" strokeWidth={1.5}/>
                  {pendingIconFile ? "Cambia icona" : (editing.icon_file_id ? "Sostituisci icona" : "Carica icona")}
                  <input type="file" className="hidden"
                         accept="image/svg+xml,image/png,image/jpeg,image/webp,.svg"
                         data-testid="picto-icon-input"
                         onChange={handleFormIconUpload} disabled={savingPicto} />
                </label>
                {pendingIconFile && <div className="text-xs text-[#525252] mt-2 break-all" data-testid="picto-icon-filename">{pendingIconFile.name}</div>}
                <div className="text-xs text-[#707070] mt-2">SVG o PNG · sono accettati anche i formati immagine precedenti · massimo 2 MB.</div>
              </div>
            </div>
          </div>}
          <ColorPickerField label="Colore del simbolo" value={editing.color_hex}
            onChange={(value) => setEditing(prev => ({ ...prev, color_hex: value }))}
            testId="picto-input-color" fallback="#1A1A1A" />
          <input className="input-luxury" type="number" placeholder="Ordine" value={editing.order_index||0}
                 data-testid="picto-input-order"
                 onChange={(e)=>setEditing({...editing, order_index: Number(e.target.value)})} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={editing.is_active} data-testid="picto-input-active" aria-label="Attivo"
                   onChange={(e)=>setEditing({...editing, is_active: e.target.checked})}/>
            <ProductStatus tone={editing.is_active ? "success" : "danger"} testId="picto-edit-status">
              {editing.is_active ? "Attivo" : "Disattivo"}
            </ProductStatus>
          </label>
          <div className="sm:col-span-2 flex justify-end gap-3">
            <button onClick={closePictoEditor} disabled={savingPicto} className="btn-ghost" data-testid="picto-cancel-btn">Annulla</button>
            <button onClick={save} disabled={savingPicto} className="btn-primary" data-testid="picto-save-btn">{savingPicto ? "Salvataggio..." : "Salva"}</button>
          </div>
        </div>
        </DialogContent>
        </Dialog>
      )}
      {ownItems.length === 0 && <p className="text-sm text-[#707070] mb-6" data-testid="tenant-pictograms-empty">Nessun pittogramma personale. Puoi usare quelli di default oppure aggiungerne uno tuo.</p>}
      {/* The personal collection contains only directly uploaded pictograms. */}
      <div className="text-xs text-[#707070] mb-4 italic" data-testid="picto-dnd-hint">
        Suggerimento: trascina un pittogramma su un'altra categoria per riassegnarlo.
      </div>
      {(() => {
        const groups = [
          ...visibleCats.map(c => ({ id: c.id, name: c.name, order_index: c.order_index, is_default: c.is_default })),
          { id: null, name: "Senza categoria", order_index: 999999 },
        ];
        return groups.map(g => {
          const catItems = ownItems.filter(p => g.id === null
            ? !displayCategoryId(p) || !visibleCats.some(c => c.id === displayCategoryId(p))
            : displayCategoryId(p) === g.id);
          const groupKey = g.id || "none";
          const isDropTarget = dragOverKey === groupKey;
          // Hide "Senza categoria" only when empty AND not currently dragging
          if (catItems.length === 0 && (g.id === null || g.is_default) && !draggingId) return null;
          return (
            <div
              key={g.id || "none"}
              className={`mb-6 rounded-sm transition ${isDropTarget ? "bg-[#F4F4F0] ring-2 ring-[#1A1A1A] ring-offset-2" : ""}`}
              data-testid={`picto-group-${g.id || "none"}`}
              onDragOver={(e) => onDragOver(e, groupKey)}
              onDragLeave={(e) => onDragLeave(e, groupKey)}
              onDrop={(e) => onDrop(e, g.id)}
            >
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#E5E5E0]">
                <div className="flex items-center gap-3 flex-wrap min-w-0">
                  <h4 className="font-serif text-lg text-[#1A1A1A]">{g.name}</h4>
                  <span className="text-xs text-[#707070] tracking-luxury uppercase">
                    {catItems.length} pittogramm{catItems.length === 1 ? "a" : "i"}
                  </span>
                  {isDropTarget && (
                    <span className="text-xs text-[#1A1A1A] tracking-luxury uppercase font-medium">
                      · rilascia qui
                    </span>
                  )}
                </div>
                {g.id && !g.is_default && (
                  <div className="flex gap-2">
                    <button onClick={()=>setEditingCat(pcats.find(c=>c.id===g.id))}
                            className="text-[#707070] hover:text-[#1A1A1A]"
                            data-testid={`pcat-edit-${g.id}`}>
                      <Edit2 className="w-3 h-3"/>
                    </button>
                    <button onClick={()=>setDelCat({ id: g.id, name: g.name })}
                            className="text-[#8C3A3A]"
                            data-testid={`pcat-delete-${g.id}`}>
                      <Trash2 className="w-3 h-3"/>
                    </button>
                  </div>
                )}
              </div>
              {catItems.length === 0 ? (
                <div className="text-xs text-[#A0A0A0] italic py-4 text-center border border-dashed border-[#E5E5E0] rounded-sm">
                  Nessun pittogramma in questa categoria. Trascinane uno qui.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
                  {catItems.map(p => (
                    <div
                      key={p.id}
                      role="button"
                      tabIndex={0}
                      draggable
                      onDragStart={(e) => onDragStart(e, p.id)}
                      onDragEnd={onDragEnd}
                      onClick={e => openPictoEditor(p, e)}
                      onKeyDown={e => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openPictoEditor(p, e); } }}
                      className={`group relative border rounded-sm bg-white cursor-grab active:cursor-grabbing transition p-2 flex flex-col items-center gap-1 select-none ${draggingId === p.id ? "opacity-40 border-[#1A1A1A]" : "border-[#E5E5E0] hover:border-[#1A1A1A] hover:shadow-sm"}`}
                      data-testid={`picto-card-${p.id}`}
                      title={`${p.name} — click per modificare, trascina per spostare`}
                    >
                      <div className="w-full aspect-square flex items-center justify-center bg-[#FAFAF7]">
                        {p.icon_file_id ? (
                          <PictogramImage pictogram={p} className="w-12 h-12 pointer-events-none"
                            testId={`picto-card-image-${p.id}`} />
                        ) : (
                          <label
                            className="cursor-pointer w-full h-full border border-dashed border-[#C5B39B] flex items-center justify-center text-[#707070] hover:border-[#1A1A1A] hover:text-[#1A1A1A]"
                            onClick={(e)=>e.stopPropagation()}
                            data-testid={`picto-upload-${p.id}`}
                          >
                            <Upload className="w-3 h-3"/>
                            <input type="file" className="hidden" accept="image/svg+xml,image/png,image/jpeg,image/webp,.svg" data-testid={`picto-upload-input-${p.id}`}
                                   onChange={(e)=>uploadIconFromCard(e,p.id)} />
                          </label>
                        )}
                      </div>
                      <div className="text-[10px] text-center leading-tight text-[#1A1A1A] w-full truncate" title={p.name}
                        data-testid={`picto-card-label-${p.id}`}>{p.name}</div>
                      <ProductStatus tone={p.is_active ? "success" : "danger"} testId={`picto-status-${p.id}`}>
                        {p.is_active ? "Attivo" : "Disattivo"}
                      </ProductStatus>
                      {!p.is_default_derivative && <button
                        onClick={(e)=>{ e.stopPropagation(); askDeletePicto(p); }}
                        className="picto-delete-control absolute top-1 right-1 w-9 h-9 flex items-center justify-center border border-[#D4D4D4] bg-white text-[#8C3A3A] hover:bg-[#FAFAFA] rounded-sm"
                        data-testid={`picto-delete-${p.id}`}
                        aria-label={`Elimina ${p.name}`}
                        title="Elimina"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
                      </button>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        });
      })()}

      {delPicto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="picto-delete-modal">
          <div className="bg-white w-full max-w-md border border-[#E5E5E0] shadow-xl">
            <div className="px-6 py-4 border-b border-[#F0F0EA]">
              <h3 className="font-serif text-lg">Elimina pittogramma</h3>
            </div>
            <div className="p-6 text-sm text-[#1A1A1A] leading-relaxed">
              Il pittogramma <strong>«{delPicto.picto.name}»</strong> è usato in <strong>{delPicto.count}</strong> prodott{delPicto.count === 1 ? "o" : "i"}. Sei sicuro di volerlo cancellare?
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#F0F0EA]">
              <button onClick={()=>setDelPicto(null)} className="btn-ghost text-sm" data-testid="picto-delete-cancel">Annulla</button>
              <button onClick={confirmDeletePicto} disabled={delBusy} className="btn-primary text-sm !bg-[#8C3A3A]" data-testid="picto-delete-confirm">
                {delBusy ? "Eliminazione..." : "Elimina pittogramma"}
              </button>
            </div>
          </div>
        </div>
      )}

      {delCat && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="pcat-delete-modal">
          <div className="bg-white w-full max-w-md border border-[#E5E5E0] shadow-xl">
            <div className="px-6 py-4 border-b border-[#F0F0EA]">
              <h3 className="font-serif text-lg">Elimina categoria</h3>
            </div>
            <div className="p-6 text-sm text-[#1A1A1A] leading-relaxed">
              Se cancelli la categoria <strong>«{delCat.name}»</strong> i pittogrammi contenuti non verranno associati a nessuna categoria. Sei sicuro di voler cancellare la categoria?
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#F0F0EA]">
              <button onClick={()=>setDelCat(null)} className="btn-ghost text-sm" data-testid="pcat-delete-cancel">Annulla</button>
              <button onClick={confirmDelCat} disabled={delBusy} className="btn-primary text-sm !bg-[#8C3A3A]" data-testid="pcat-delete-confirm">
                {delBusy ? "Eliminazione..." : "Elimina categoria"}
              </button>
            </div>
          </div>
        </div>
      )}
      </section>
    </div>
  );
}

/**
 * Combined colour picker + hex text input that stay in sync.
 * The user can either pick from the native colour dialog or type the hex
 * value manually (validated as 3 or 6 hex digits with optional '#').
 */
function ColorPickerField({ label, value, onChange, testId, fallback }) {
  const safe = value || fallback || "#000000";
  const [text, setText] = useState(safe);

  useEffect(() => { setText(safe); }, [safe]);

  const normalise = (v) => {
    let s = (v || "").trim();
    if (!s.startsWith("#")) s = "#" + s;
    // Expand 3-digit shortcut
    const three = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(s);
    if (three) s = `#${three[1]}${three[1]}${three[2]}${three[2]}${three[3]}${three[3]}`;
    return s;
  };
  const isValidHex = (v) => /^#[0-9a-f]{6}$/i.test(v);

  const handleText = (v) => {
    setText(v);
    const n = normalise(v);
    if (isValidHex(n)) onChange(n.toLowerCase());
  };

  return (
    <div>
      <label className="label-tiny">{label}</label>
      <div className="flex items-center gap-2">
        <input type="color"
               value={isValidHex(normalise(text)) ? normalise(text) : safe}
               onChange={(e)=>{ setText(e.target.value); onChange(e.target.value); }}
               className="w-12 h-10 border border-[#E5E5E0] cursor-pointer shrink-0"
               data-testid={`${testId}-picker`}/>
        <input type="text"
               value={text}
               onChange={(e)=>handleText(e.target.value)}
               onBlur={(e)=>{
                 const n = normalise(e.target.value);
                 if (isValidHex(n)) { setText(n); onChange(n.toLowerCase()); }
                 else setText(safe); // revert on invalid
               }}
               placeholder="#RRGGBB"
               maxLength={7}
               spellCheck={false}
               className="input-luxury flex-1 font-mono uppercase text-sm"
               data-testid={`${testId}-hex`}/>
        <span
          className="w-8 h-8 border border-[#E5E5E0] shrink-0 rounded-sm"
          style={{ background: safe }}
          data-testid={`${testId}-swatch`}
          title={safe}
        />
      </div>
    </div>
  );
}

